import React from 'react';

const Equation = ({ label, children }) => (
  <div className="paper-equation" role="img" aria-label={label}>
    {children}
  </div>
);

const InlineMath = ({ children }) => (
  <span className="paper-inline-math">{children}</span>
);

const V = ({ children }) => (
  <span className="math-var">{children}</span>
);

const PaperSection = ({ title, children }) => (
  <section className="paper-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const PaperSubsection = ({ title, children }) => (
  <section className="paper-subsection">
    <h3>{title}</h3>
    {children}
  </section>
);

const Figure = ({ src, caption, alt, width, height }) => (
  <figure className="paper-figure">
    <div className="paper-figure-scroll">
      <img src={src} alt={alt || caption} loading="lazy" width={width} height={height} />
    </div>
    <figcaption>{caption}</figcaption>
  </figure>
);

const Table = ({ caption, headers, rows, note }) => (
  <figure className="paper-table-figure">
    <div className="paper-table-wrap">
      <table className="paper-table">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.join('-')}>
              {row.map((cell, index) => (
                <td key={`${headers[index]}-${cell}`} data-label={headers[index]}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <figcaption>
      {caption}
      {note && <span> {note}</span>}
    </figcaption>
  </figure>
);

export const FseoPaper = () => (
  <div className="paper-content">
    <section className="paper-abstract">
      <p>
        We introduce a perpetual derivative that provides options-like convex payoffs without requiring
        strike selection or expiry management. The instrument uses a floating strike defined as an
        exponentially weighted moving average of the spot price, combined with the everlasting options
        funding mechanism (White and Bankman-Fried, 2021). The result is a single market per asset per
        direction that concentrates all convexity-seeking liquidity into one venue. We validate the
        mechanism on 365 days of minute-level BTC and ETH price data. The instrument delivers measurable
        downside protection (1st percentile losses 30-42% smaller than equivalent perp positions), positive
        P&amp;L skewness (0.87 for 4-hour holds), and attractive short-side economics (91% win rate for
        delta-hedged sellers). Daily funding costs range from 0.19% to 1.02% of notional depending on the
        half-life parameter.
      </p>
    </section>

    <PaperSection title="Introduction">
      <p>
        Perpetual futures solved a fundamental problem in derivatives markets. Before perps, futures
        fragmented liquidity across multiple expiration dates. Traders who wanted indefinite exposure had
        to repeatedly roll their positions, paying spread costs and bearing execution risk at each roll. The
        perpetual future collapsed all expiry-based fragmentation into a single market per asset by replacing
        discrete rolling with a continuous funding rate.
      </p>
      <p>
        Options suffer from a worse version of the same problem. They fragment along two dimensions. A
        typical options chain contains 20 or more strike prices across 6 or more expiration dates, for both
        calls and puts. This produces over 240 separate markets per asset. Each market requires its own
        liquidity. The result is wide spreads, thin order books, and a product that is inaccessible to the
        majority of crypto traders.
      </p>
      <p>
        White and Bankman-Fried (2021) introduced everlasting options, which apply the perpetual funding
        rate mechanism to options payoffs. This eliminates expiry fragmentation. However, the original
        formulation still requires separate markets for each strike price. In the same paper, the authors
        briefly suggest a floating-strike variant using an exponentially weighted moving average of the spot
        price as the strike. This would eliminate strike fragmentation as well, collapsing the entire options
        surface into a single market per asset per direction.
      </p>
      <p>
        Nobody has built this. The first implementation of everlasting options, Squeeth by Opyn, used a
        quadratic payoff (price squared) rather than a floating-strike linear payoff. Squeeth&apos;s funding
        costs were prohibitively high due to the mathematical properties of quadratic convexity, and the
        product failed commercially. This discouraged further work on the everlasting options framework.
      </p>
      <p>
        This essay develops the floating-strike everlasting option in full, provides a complete mechanism
        description, and validates it empirically on one year of minute-level price data.
      </p>
    </PaperSection>

    <PaperSection title="Background">
      <PaperSubsection title="Perpetual Futures">
        <p>
          A perpetual future tracks the spot price of an underlying asset through a funding rate mechanism.
          At regular intervals, a payment is transferred between long and short position holders.
        </p>
        <Equation label="funding equals mark minus index">
          funding = mark - index
        </Equation>
        <p>
          If the perp trades above spot, longs pay shorts, creating selling pressure. If below, shorts pay
          longs, creating buying pressure. This acts as an economic spring that keeps the perp tethered to
          spot.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Everlasting Options">
        <p>
          White and Bankman-Fried generalized the perpetual funding mechanism to arbitrary payoff
          functions. For any payoff P(t), a perpetual derivative can be constructed with funding of the form:
        </p>
        <Equation label="funding equals mark minus payoff divided by n">
          funding = (mark - payoff) / <V>n</V>
        </Equation>
        <p>
          where n is the number of funding payments per period. They prove that this perpetual derivative
          is equivalent to a geometrically weighted basket of expiring derivatives with the same payoff. The
          proof uses a no-arbitrage argument and holds for any well-defined payoff function.
        </p>
      </PaperSubsection>
    </PaperSection>

    <PaperSection title="Mechanism">
      <PaperSubsection title="The Floating Strike">
        <p>
          We define the strike as a trailing exponentially weighted moving average of the spot price,
          computed recursively:
        </p>
        <Equation label="M of t equals lambda times S of t plus one minus lambda times M of t minus one">
          <V>M</V>(<V>t</V>) = λ · <V>S</V>(<V>t</V>) + (1 - λ) · <V>M</V>(<V>t</V> - 1)
        </Equation>
        <p>
          where <InlineMath><V>S</V>(<V>t</V>)</InlineMath> is spot price at time t and{' '}
          <InlineMath>λ = 1 - 2<sup>-Δt/h</sup></InlineMath>. The parameter h is the half-life.
          After h units of time at a constant new price, the gap between EWMA and spot closes by exactly
          half. The half-life is the only tunable parameter.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Payoff">
        <p>The FSEO call payoff is:</p>
        <Equation label="call payoff equals max of S of t minus M of t and zero">
          payoff<sub>call</sub>(<V>t</V>) = max(<V>S</V>(<V>t</V>) - <V>M</V>(<V>t</V>), 0)
        </Equation>
        <p>The FSEO put payoff is:</p>
        <Equation label="put payoff equals max of M of t minus S of t and zero">
          payoff<sub>put</sub>(<V>t</V>) = max(<V>M</V>(<V>t</V>) - <V>S</V>(<V>t</V>), 0)
        </Equation>
        <p>
          When spot exceeds the EWMA, the call has positive payoff. When EWMA exceeds spot, the put has
          positive payoff. Neither can go below zero. This asymmetry is convexity.
        </p>
        <p>
          The floating strike means the payoff responds to deviations from recent price history. If the price
          jumps suddenly, the EWMA lags behind and the payoff becomes large. As the EWMA catches up, the
          payoff decays. This decay is analogous to theta in traditional options.
        </p>
        <Figure
          src="/fseo-figure-1.png"
          caption="BTC spot vs EWMA at half-lives of 1h, 4h, and 24h during a volatile period; FSEO call and put payoffs at 24h half-life."
          alt="BTC spot versus EWMA and FSEO call and put payoffs."
          width={1979}
          height={678}
        />
      </PaperSubsection>

      <PaperSubsection title="Funding Rate">
        <p>Funding is paid from longs to shorts at regular intervals:</p>
        <Equation label="funding equals mark minus payoff divided by n">
          funding = (mark - payoff) / <V>n</V>
        </Equation>
        <p>
          Mark exceeds payoff because the instrument has optionality value beyond its intrinsic value. The
          difference is time value, paid continuously as funding. This is economically equivalent to
          continuous option premium.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Equivalence to Everlasting Options Framework">
        <p>
          The payoff function max(S(t) - M(t), 0) is well-defined at every point in time. The
          White/Bankman-Fried proof applies without modification. The FSEO is equivalent to a
          geometrically weighted basket of expiring floating-strike Asian options. Under Black-Scholes
          assumptions with hourly funding, the FSEO behaves approximately like a floating-strike option
          expiring in <InlineMath><V>T</V><sub>eff</sub> = h / ln(2)</InlineMath> minutes,
          continuously rolled.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Properties">
        <p>
          One market per asset per direction. No strike selection. No expiry management. All
          convexity-seeking liquidity concentrates in a single venue. The payoff is convex with gains
          captured linearly and losses bounded at zero before funding. The funding rate serves as a
          transparent, continuous measure of implied volatility. The half-life parameter controls the
          tradeoff between responsiveness and cost.
        </p>
      </PaperSubsection>
    </PaperSection>

    <PaperSection title="Empirical Validation">
      <PaperSubsection title="Data">
        <p>
          We use minute-level BTC/USD and ETH/USD spot prices from March 23, 2025 to March 23, 2026.
          BTC: 525,660 observations, $60,039 to $126,202 range, 43.6% annualized realized volatility. ETH:
          526,631 observations, $1,386 to $4,953 range, 71.8% annualized realized volatility.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Funding Rates">
        <p>
          We estimate funding rates by computing rolling realized volatility of the spot-minus-EWMA gap and
          pricing time value using the standard ATM option approximation.
        </p>
        <Table
          caption="Daily and annualized funding costs."
          note="Annualized figures assume continuous holding, which is not the intended use case."
          headers={['Half-life', 'BTC daily', 'BTC annual', 'ETH daily', 'ETH annual']}
          rows={[
            ['1h', '0.19%', '68%', '0.31%', '115%'],
            ['4h', '0.39%', '143%', '0.66%', '241%'],
            ['12h', '0.71%', '258%', '1.18%', '431%'],
            ['24h', '1.02%', '371%', '1.69%', '618%'],
          ]}
        />
        <Figure
          src="/fseo-figure-2.png"
          caption="Daily funding cost by half-life, plus P&L distribution of FSEO call vs perp for BTC, 4h hold, 4h half-life. FSEO shows positive skewness (0.87), confirming convexity."
          alt="Funding costs and FSEO call versus perp profit and loss distribution."
          width={1979}
          height={738}
        />
      </PaperSubsection>

      <PaperSubsection title="Convexity">
        <p>
          The chart below plots FSEO call P&amp;L against underlying price change over 4-hour holds. The FSEO
          curve lies above the linear diagonal on the downside (losses are smaller) and approximately tracks
          it on the upside (gains are preserved).
        </p>
        <Figure
          src="/fseo-figure-3.png"
          caption="FSEO call P&L vs price change for BTC and ETH. 4-hour hold, 4-hour half-life. Green curve is the FSEO average. Diagonal is a linear perp."
          alt="FSEO call profit and loss versus price change for BTC and ETH."
          width={1980}
          height={777}
        />
      </PaperSubsection>

      <PaperSubsection title="Downside Protection">
        <Table
          caption="Tail risk comparison, BTC, 24h half-life."
          note="The FSEO reduces 1st percentile losses by 27-42%."
          headers={['Hold period', 'Perp 1st pctile', 'FSEO 1st pctile', 'Protection']}
          rows={[
            ['4h', '-2.02%', '-1.18%', '42%'],
            ['12h', '-3.63%', '-2.21%', '39%'],
            ['24h', '-5.15%', '-3.45%', '33%'],
            ['3d', '-9.31%', '-6.82%', '27%'],
          ]}
        />
        <Figure
          src="/fseo-figure-4.png"
          caption="1st percentile loss magnitude for perp vs FSEO across holding periods."
          alt="First percentile loss magnitude for perp versus FSEO."
          width={1379}
          height={678}
        />
      </PaperSubsection>

      <PaperSubsection title="Short-Side Economics">
        <p>
          A delta-hedged short FSEO position earns funding in exchange for convexity risk. Simulated with
          5-minute hedge rebalancing:
        </p>
        <Table
          caption="Delta-hedged short P&L, BTC, 24h half-life."
          note="*Sharpe ratios are overstated due to idealized simulation (zero slippage, perfect hedging). A 3-5x haircut still yields attractive risk-adjusted returns."
          headers={['Hold', 'Win rate', 'Avg P&L', 'Ann. Sharpe*']}
          rows={[
            ['24h', '90.9%', '+1.05%', '22.3'],
            ['3d', '98.3%', '+3.15%', '17.1'],
            ['7d', '100%', '+7.37%', '16.0'],
          ]}
        />
      </PaperSubsection>
    </PaperSection>

    <PaperSection title="Comparison to Existing Instruments">
      <PaperSubsection title="Versus Perpetual Futures">
        <p>
          A perpetual future has a linear, symmetric payoff. The FSEO has a convex, asymmetric payoff. The
          tradeoff is continuous funding. The FSEO is not a perp replacement. It serves traders who want
          leveraged directional exposure with defined risk, portfolio insurance, or volatility trading.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Versus Traditional Options">
        <p>
          Deribit requires selecting from over 240 strike-expiry combinations per asset. The FSEO has one
          market per asset per direction. Precision is traded for simplicity and liquidity concentration. On
          cost, the FSEO compares favorably for users who roll short-dated ATM options. A daily ATM
          straddle on Deribit costs approximately 2-3% of notional including spread. The FSEO at 24h
          half-life costs approximately 1% per day with no roll spread.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Versus Squeeth">
        <p>
          Squeeth used a quadratic payoff (price squared). Its funding scaled with sigma squared, reaching
          50-100% annualized. The FSEO uses a linear payoff with funding scaling as sigma times the square
          root of effective duration. At 4h half-life, BTC daily funding is 0.39%, or 0.065% per 4-hour
          trade.
        </p>
      </PaperSubsection>
    </PaperSection>

    <PaperSection title="Implementation">
      <p>
        The EWMA requires a continuous spot price feed. Its smoothing provides partial protection against
        oracle manipulation. FSEO longs face gradual margin erosion from funding, not sudden liquidation
        from price moves. This gives the liquidation engine more time to act. FSEO shorts face unlimited
        payoff expansion and must be margined accordingly. A 4-hour half-life is a reasonable starting
        point, offering 0.39% daily funding and breakeven moves under 0.40%.
      </p>
    </PaperSection>

    <PaperSection title="Why Now">
      <p>
        The theoretical foundation has existed since 2021. Several factors explain the delay. Squeeth&apos;s
        failure created a false negative for the category. Its prohibitive funding was a consequence of
        quadratic payoffs, not the everlasting mechanism. On-chain infrastructure was not mature enough in
        2021. Hyperliquid has since proven purpose-built chains can achieve required throughput. The market
        maker ecosystem has matured. Delta-hedged strategies require sophisticated participants who now
        operate on-chain. The addressable market has grown. Millions of perp traders are comfortable with
        funding rate mechanics.
      </p>
      <p>
        The FSEO is designed for perp traders who would benefit from convexity but find traditional options
        inaccessible. The instrument looks like a perp. You choose long or short, deposit margin, monitor
        your position. The difference is bounded downside, paid for through continuous funding.
      </p>
    </PaperSection>

    <PaperSection title="Conclusion">
      <p>
        The floating-strike everlasting option collapses the entire options surface into a single market per
        asset per direction. The mechanism is grounded in the everlasting options framework, which provides
        a general no-arbitrage pricing proof. Empirical validation on one year of BTC and ETH data confirms
        measurable convexity, meaningful downside protection, sustainable funding for short-term traders,
        and attractive economics for liquidity providers.
      </p>
    </PaperSection>

    <section className="paper-references">
      <h2>Sources</h2>
      <ol>
        <li>White, D. and Bankman-Fried, S. (2021). Everlasting Options. Paradigm Research.</li>
        <li>Robinson, D., Clark, J., and Leone, A. (2024). Everything Is a Perp. Paradigm Research.</li>
        <li>Clark, J., Robinson, D., and White, D. (2021). Power Perpetuals. Paradigm Research.</li>
        <li>Black, F. and Scholes, M. (1973). The Pricing of Options and Corporate Liabilities. Journal of Political Economy 81(3), 637-654.</li>
      </ol>
    </section>
  </div>
);

export const AdversarialVerificationPaper = () => (
  <div className="paper-content">
    <section className="paper-abstract">
      <p>
        We describe a protocol for producing verified security properties of smart contracts through
        adversarial economic competition. The protocol defines two actions. Attest, which is to stake
        capital on a property being true and earn yield. Or challenge, which is to submit a counterexample
        that violates the property and earn the bounty and all attester stakes. Resolution is programmatic.
        No committee. No oracle. The protocol creates two things that do not exist today. An economic path
        for security researchers to earn from positive assessments, not only from discovered vulnerabilities.
        And a composable, on-chain trust primitive that other contracts can query and act on.
      </p>
    </section>

    <PaperSection title="Problem">
      <p>
        In 2025, $3.35 billion was stolen from Web3 protocols across 630 incidents. Every major exploit
        occurred on audited code. The audit market approaches $1 billion in annual revenue. Losses grow
        faster than security spending.
      </p>
      <p>
        Audit firms sell time, not outcomes. A protocol pays $300,000 for a report. If exploited the next
        day, the auditors bear no loss. Bug bounty platforms pay for discovered vulnerabilities but not for
        thorough review that finds nothing. A researcher who spends 200 hours confirming code is clean
        earns zero. Dispute resolution depends on mediation or committees. No existing mechanism pays for
        outcomes, rewards positive assessments, and resolves disputes programmatically.
      </p>
    </PaperSection>

    <PaperSection title="Mechanism">
      <p>
        Three participants. The protocol team that wants its code verified. Attesters who review code and
        stake capital on properties they believe hold. Challengers who attempt to produce counterexamples.
      </p>

      <PaperSubsection title="Properties">
        <p>
          A property is a testable invariant about a specific smart contract at a specific code version. A
          function that takes a transaction sequence as input and returns true or false. Most DeFi exploits
          come from a small number of vulnerability classes. A set of 5 to 15 properties covering
          reentrancy, oracle manipulation, access control, and accounting logic captures the majority of
          real attack vectors.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Funding">
        <p>
          The protocol team deposits capital into a pool for each property and sets a stream rate in tokens
          per block. The deposit serves as the source of attester yield and as the bounty for challengers.
          Yield streams continuously from the deposit to attesters. The remaining deposit at any moment is
          part of the challenger reward if the property is broken.
        </p>
        <p>
          The protocol team can top up the deposit at any time. They can adjust the stream rate for
          unchanged code to increase or decrease scrutiny. When they stop streaming, a grace period keeps
          the remaining deposit locked for challengers. After the grace period with no successful challenge,
          unspent funds are reclaimable.
        </p>
        <p>
          Code upgrades are handled separately. Old properties may not apply to new bytecode. The protocol
          team stops old streams, defines new properties with fresh deposits. Old verifications remain on
          chain as permanent records of the previous version.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Attestation">
        <p>
          An attester deposits capital against a specific property. If a challenger breaks the property, the
          attester loses their entire stake. In return, the attester earns a share of the stream proportional
          to their fraction of total attestation capital. Let <InlineMath><V>S</V></InlineMath> be the
          stream rate, <InlineMath><V>A</V><sub>i</sub></InlineMath> attester i&apos;s stake,{' '}
          <InlineMath><V>A</V></InlineMath> the total. Attester i earns{' '}
          <InlineMath><V>S</V> · <V>A</V><sub>i</sub> / <V>A</V></InlineMath> per block.
        </p>
        <p>
          Attesters can withdraw at any time when no challenge is pending. Challenges freeze stakes until
          resolution, which takes seconds to minutes. There are no lock periods. Total attestation capital
          floats to the level where yield matches perceived risk. High attestation and low yield means the
          market is confident. Low attestation and high yield means the market sees risk. The equilibrium is
          a continuous, market-derived risk price for smart contract security.
        </p>
      </PaperSubsection>

      <PaperSubsection title="Challenge">
        <p>
          A challenger submits a concrete transaction sequence that violates the property. They post a small
          bond to prevent spam. Verification is programmatic and binary. The protocol executes the
          counterexample and checks the property condition.
        </p>
        <p>
          If violated, the challenger earns the remaining deposit plus all attester stakes. All attested
          capital is slashed. If not violated, the challenge is rejected and the bond is forfeited.
        </p>
      </PaperSubsection>
    </PaperSection>

    <PaperSection title="Verification">
      <p>
        When a challenger submits a counterexample, the protocol forks the contract state, applies the
        transaction sequence, and evaluates the property. The result is boolean. For gas efficiency,
        verification can occur off chain with an on-chain commitment using zero knowledge proofs of
        execution traces.
      </p>
      <p>
        Audit firms produce subjective severity ratings. Bug bounty platforms rely on mediation. Insurance
        products use committees. All introduce human judgment at resolution. Programmatic verification
        removes it entirely.
      </p>
    </PaperSection>

    <PaperSection title="Economics">
      <p>
        An attester&apos;s expected return depends on yield and the probability that a vulnerability exists
        that they missed. Let y be annualized yield and p the estimated miss probability. Expected annual
        return is y * (1 - p) - p. Thorough review reduces p. Lower p means higher risk-adjusted return.
        The mechanism converts self-interest into security work.
      </p>
      <p>
        The challenger&apos;s expected payoff is{' '}
        <InlineMath>(<V>D</V> + <V>A</V>) · <V>q</V> - <V>c</V></InlineMath>, where{' '}
        <InlineMath><V>D</V></InlineMath> is remaining deposit, <InlineMath><V>A</V></InlineMath> is
        total attestation, <InlineMath><V>q</V></InlineMath> is probability of finding a counterexample,
        and <InlineMath><V>c</V></InlineMath> is investigation cost. As
        attestation grows, the reward for breaking a property grows proportionally. For long tail contracts,
        AI agents serve as the marginal challenger. Their cost approaches zero.
      </p>
      <p>
        A protocol with $200 million in TVL currently spends roughly $520,000 per year on security. With
        this protocol they spend $500 per day across 10 properties plus $100,000 in deposits. First-year
        cost around $280,000. For less money they get continuous verification, programmatic resolution, and
        a composable on-chain security profile. A $500 per day stream may attract $2,000,000 in
        attestation. The protocol spent $15,000 in a month and bought $2,000,000 in economic
        accountability.
      </p>
    </PaperSection>

    <PaperSection title="Trust Layer">
      <p>
        Every surviving property produces an on-chain record. Property specification, total attestation
        capital, survival duration, failed challenge count, stream rate. The aggregate is a security profile
        queryable by other smart contracts.
      </p>
      <p>
        A lending protocol can require that collateral tokens have minimum attestation across minimum
        properties with minimum survival duration. Insurance protocols can price off verification coverage.
        Bridges can require thresholds before supporting new assets. Each integration increases demand for
        verification, which increases scrutiny, which increases credibility.
      </p>
      <p>
        Composability risk is the largest unsolved problem in DeFi. Protocol A integrates with Protocol B
        and has no way to programmatically assess B&apos;s security. The composable trust output of this
        protocol is a direct solution. Verification produced once by the market. Trust consumed by every
        protocol that queries it.
      </p>
    </PaperSection>

    <PaperSection title="Why Not a Prediction Market">
      <p>
        A prediction market on whether a protocol will be exploited does not cause anyone to review code.
        It does not produce verified properties. A participant who discovers a vulnerability profits most by
        exploiting it while holding the right position. Resolution requires an oracle. The output is a
        probability, not a composable fact.
      </p>
      <p>
        This protocol inverts those properties. Resolution is programmatic. The output is a verified fact.
        A challenger earns more from submitting a counterexample than from exploitation. Prediction markets
        produce prices. This protocol produces facts.
      </p>
    </PaperSection>

    <PaperSection title="Future Work">
      <p>
        The mechanism applies staking and slashing to the verification of smart contract properties. The
        pattern is not new. Proof of stake systems and optimistic rollups use the same primitives. What is
        new is the application to arbitrary security properties with a permissionless, yield-bearing attester
        role and composable on-chain output.
      </p>
      <p>
        The same mechanism may extend to verification of other computational claims where properties are
        testable and counterexamples are cheap to verify. AI-generated smart contracts are the most natural
        extension. As AI agents produce more code, the need for adversarial quality assurance at scale grows.
        Whether the mechanism generalizes beyond smart contracts is an empirical question that depends on
        execution, not on theory.
      </p>
    </PaperSection>

    <PaperSection title="Conclusion">
      <p>
        This protocol creates two things that do not exist in smart contract security today. A market where
        researchers earn from positive assessments, activating security work that currently does not happen.
        And composable on-chain trust primitives that other contracts can consume programmatically, solving
        the composability risk problem in DeFi.
      </p>
      <p>
        The mechanism is simple. Properties, deposits, streams, attestation, challenge, programmatic
        resolution. The novelty is not in any single component but in their combination, which produces an
        economic role and an on-chain output that no existing system generates.
      </p>
    </PaperSection>
  </div>
);
